import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { UserService } from '../../user/services/user.service';
import { LoaderService } from '../../services/loader.service';
import { ClaimsService } from '../../policy-dashboard/services/claims.service';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { POLICY_CLAIMS_BATCH_SIZE } from '../../shared/constants';

/** Claim details */
interface ClaimDetails {
  /** The claim number */
  id: string;
  /** The claim dropdown text */
  text: string;
  /** The date of loss */
  dateOfLoss: string;
  /** The loss location */
  lossLocation: string;
}

/**
 * Message Us modal component
 */
@Component({
  selector: 'app-message-us-modal',
  templateUrl: './message-us-modal.component.html',
  styleUrls: ['./message-us-modal.component.scss']
})
export class MessageUsModalComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** The message us modal */
  @ViewChild('messageUsModal') messageUsModal: ModalDirective;

  /** The customer details */
  @Input() customerDetails: any;
  /** The list of policy numbers */
  @Input() policyList: string[] = [];
  /** Emits when the modal is closed */
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  /** The max message length allowed */
  public readonly maxMessageLength: number = 5000;

  /** Whether the message is claims-related */
  public isClaimsRelated: boolean = null;

  /** The list of claim details */
  public claimDetails: ClaimDetails[] = [];
  /** The selected claim number */
  public selectedClaimId: string;
  /** The selected policy number */
  public selectedPolicy: string;
  /** The message to send */
  public messageText: string;

  /** Whether to show the Cancel Confirmation dialog */
  public showCancelConfirm: boolean = false;
  /** Whether to show the Error dialog */
  public showErrorModal: boolean = false;
  /** The error message to show */
  public errorMessage: string;

  /** The subscriptions */
  private subscriptions: Subscription = new Subscription();

  constructor(
    private userService: UserService,
    private claimsService: ClaimsService,
    private spinner: LoaderService,
    private router: Router) {
  }

  /** @inheritdoc */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['policyList'] && this.policyList) {
      this.resetPolicyDropdown();
      this.fetchClaims();
    }
  }

  /** @inheritdoc */
  ngAfterViewInit(): void {
    this.messageUsModal.show();

    this.subscriptions.add(
      this.messageUsModal.onHide.subscribe(() => {
        this.closed.emit();
        this.showCancelConfirm = false;
        this.showErrorModal = false;
      }
    ));

    this.subscriptions.add(
      this.messageUsModal.onHidden.subscribe(() => {
        this.resetModalValues();
      })
    );
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Routes to a policy's FNOL page
   */
  public routeToFnol() {
    if (!this.selectedPolicy) {
      return;
    }

    this.messageUsModal.hide();
    this.router.navigate([`/policy-dashboard/${this.selectedPolicy}/claims/new-claim`]);
  }

  /**
   * Submits the modal
   */
  public submitMessageUsModal() {
    if (this.customerDetails) {
      const data = {
        userName: this.customerDetails.username,
        firstName: this.customerDetails.firstName,
        lastName: this.customerDetails.lastName,
        email: this.customerDetails.email,
        phone: '',
        isClaimsRelated: this.isClaimsRelated,
        claimNum: '',
        policyNum: '',
        formattedAddress: '',
        message: this.messageText
      }

      // Determine which phone number to use
      if (this.customerDetails.cellPhoneNumber === 'YES') {
        data.phone = this.customerDetails.policies[0].contactInformationList.find((c: any) => c.contactType === 'M' && c.primary)?.contactInfo;
      } else {
        data.phone = this.customerDetails.policies[0].contactInformationList.find((c: any) => c.contactType === 'H')?.contactInfo;
      }

      if (!data.phone) { // Try to grab any phone number as a fallback
        data.phone = this.customerDetails.policies[0].contactInformationList.find((c: any) => c.contactType !== 'E')?.contactInfo;
      }

      if (this.isClaimsRelated) {
        const selectedClaimDetails = this.claimDetails.find(c => c.id === this.selectedClaimId);
        data.claimNum = selectedClaimDetails?.id ?? '';
        if (data.claimNum) {
          data.formattedAddress = selectedClaimDetails?.lossLocation ?? '';
        }
      } else {
        data.policyNum = this.selectedPolicy ?? '';
        if (data.policyNum) {
          const policyData = this.customerDetails.policies.find((policy: any) => policy.policyNumber === data.policyNum);
          data.formattedAddress = policyData?.address?.formattedAddress ?? '';
        }
      }

      this.spinner.showLoader(true);
      const failureMessage = 'Failed to send message. Please try again later.';
      this.userService.sendContactUsEmail(data).subscribe(
        (res: any) => {
          this.spinner.showLoader(false);
          if (res?.status) {
            this.messageUsModal.hide();
          } else {
            this.errorMessage = failureMessage;
            this.showErrorModal = true;
          }
        },
        err => {
          this.spinner.showLoader(false);
          this.errorMessage = failureMessage;
          this.showErrorModal = true;
          console.log('Error sending Contact Us email:', err);
        });
    } else {
      this.errorMessage = 'There was an issue with retrieving your user details to send a message. Please try again later.';
      this.showErrorModal = true;
    }
  }

  /**
   * Cancels the modal
   */
  public cancelMessageUsModal() {
    // If the user wrote something to send, double check they meant to cancel
    if (this.messageText?.length > 0) {
      this.showCancelConfirm = true;
    } else {
      this.messageUsModal.hide();
    }
  }

  /**
   * Confirms the cancel action
   */
  public confirmCancel() {
    this.showCancelConfirm = false;
    this.messageUsModal.hide();
  }

  /**
   * Resets the modal values back the defaults
   */
  private resetModalValues() {
    this.isClaimsRelated = null;
    this.resetClaimsDropdown();
    this.resetPolicyDropdown();
    this.messageText = '';
  }

  /**
   * Resets the claims dropdown based on the claims list
   */
  private resetClaimsDropdown() {
    // If there is only one claim, select it automatically
    if (this.claimDetails?.length === 1) {
      this.selectedClaimId = this.claimDetails[0].id;
    } else {
      this.selectedClaimId = '';
    }
  }

  /**
   * Resets the policy dropdown based on the policy list
   */
  private resetPolicyDropdown() {
    // If there is only one policy, select it automatically
    if (this.policyList?.length === 1) {
      this.selectedPolicy = this.policyList[0];
    } else {
      this.selectedPolicy = '';
    }
  }

  /**
   * Gets the list of claims for all policies
   */
  private fetchClaims() {
    this.spinner.showLoader(true);
    this.claimDetails = [];
    this.resetClaimsDropdown();

    const batchSize = POLICY_CLAIMS_BATCH_SIZE; // Number of policies per batch
    const policyBatches = [];

    // Split policies into batches
    for (let i = 0; i < this.policyList.length; i += batchSize) {
      policyBatches.push(this.policyList.slice(i, i + batchSize));
    }

    // Convert all observables to promises
    const batchPromises = policyBatches.map(batch =>
      this.claimsService.getClaimsByPolicies(batch).toPromise()
    );

    // Use Promise.all to wait for all promises to resolve
    const claims = [];
    Promise.all(batchPromises)
      .then(claimsResults => {
        const datePipe = new DatePipe('en-US');
        const flattenedData = claimsResults.map(res => res.data).flat();
        flattenedData.forEach((policyClaims: any) => {
          policyClaims.claimWithMergedPaymentsAndRecoveriesDtos.forEach((c: any) => {
            const claimToAdd: ClaimDetails = {
              id: c.number,
              text: `${c.number} - ${c.causeOfLoss} - ${(datePipe.transform(c.dateOfLoss))}`,
              dateOfLoss: c.dateOfLoss,
              lossLocation: c.lossLocation
            }

            claims.push(claimToAdd);
          });
        });

        // Sort by Date of Loss with most recent first
        claims.sort((a, b) => {
          const dateA = new Date(a.dateOfLoss).getTime();
          const dateB = new Date(b.dateOfLoss).getTime();
          return dateB - dateA;
        });
      })
      .catch(error => {
        console.error('Error fetching claims:', error);
      })
      .finally(() => {
        this.spinner.showLoader(false);
        this.claimDetails = claims;
        this.resetClaimsDropdown();
      });
  }
}
